import React from "react";

import { useKey } from "@/client/hooks/useKey";

/**
 * Slide-in drawer from the right side.
 * Renders a backdrop, a panel with a header (title + close button), and children.
 */
export function SideDrawer({
  title,
  icon,
  onClose,
  toolbar,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  onClose: () => void;
  /** Optional toolbar row between header and content */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  useKey("Escape", onClose);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 animate-[fade-in_0.2s_ease]" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-term-bg/70 backdrop-blur-xl border-l border-term-border shadow-2xl flex flex-col animate-[slide-in-right_0.2s_ease]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-term-border bg-term-surface shrink-0">
          <h2 className="font-serif text-lg text-term-text flex items-center gap-2">
            {icon}
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-term-text-muted hover:text-term-text cursor-pointer w-7 h-7 flex items-center justify-center rounded-full hover:bg-term-raised transition-all text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Optional toolbar */}
        {toolbar && (
          <div className="flex items-center justify-between px-5 py-3 border-b border-term-border/50 bg-term-surface/50 shrink-0">
            {toolbar}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-[8rem]">{children}</div>
      </div>
    </div>
  );
}
